import { allocateProportionally } from './money'
import { divRound, mulDivRound, toSafeNumber } from './rounding'
import type { Paisa } from './money'
import type { Milli } from './qty'

// What a delivery really cost (docs/plans/free-goods.md). A receipt line keeps the paid quantity
// and the bill's rate; free goods, a discount and a charge on the free goods change its amount
// and so the true cost that stock, batches and profit use.

/** A box that takes a percent or exact rupees. */
export interface PercentOrAmount {
  mode: 'percent' | 'amount'
  /** Basis points, in percent mode. */
  bp: number | null
  /** Exact paisa, in amount mode. */
  paisa: number
}

/** `percent` of qty × rate. */
function shareOfLine(qtyMilli: Milli, ratePaisa: Paisa, bp: number): Paisa {
  return toSafeNumber(divRound(BigInt(qtyMilli) * BigInt(ratePaisa) * BigInt(bp), 1000n * 10_000n))
}

/** A line discount (of qty × rate) or a charge on free goods (of free × rate), in paisa. */
export function lineExtraPaisa(qtyMilli: Milli, ratePaisa: Paisa, box: PercentOrAmount | null) {
  if (!box) return 0
  return box.mode === 'percent' ? shareOfLine(qtyMilli, ratePaisa, box.bp ?? 0) : box.paisa
}

/** A discount on the whole bill, in paisa. */
export function billDiscountPaisa(linesTotal: Paisa, box: PercentOrAmount | null): Paisa {
  if (!box) return 0
  const off = box.mode === 'percent' ? mulDivRound(linesTotal, box.bp ?? 0, 10_000) : box.paisa
  return Math.min(off, linesTotal)
}

export interface ReceiptLineMoney {
  qtyMilli: Milli
  freeMilli: Milli
  ratePaisa: Paisa
  discountPaisa: Paisa
  freeChargePaisa: Paisa
  /** The amount as typed, when the line was entered in another unit than stock's. */
  amountPaisa?: Paisa | null
}

/** Paid goods less their discount, plus what was charged on the free goods. */
export function receiptLineAmount(l: Omit<ReceiptLineMoney, 'freeMilli'>): Paisa {
  if (l.amountPaisa != null) return l.amountPaisa
  const paid = mulDivRound(l.qtyMilli, l.ratePaisa, 1000)
  return Math.max(0, paid - l.discountPaisa) + l.freeChargePaisa
}

/** What a stored line came to: a line with nothing extra has no stored amount. */
export function storedLineAmount(l: {
  qty_milli: Milli
  unit_cost_paisa: Paisa
  amount_paisa?: Paisa | null
}): Paisa {
  return l.amount_paisa ?? mulDivRound(l.qty_milli, l.unit_cost_paisa, 1000)
}

/** Each line's amount after its share of the bill's discount, shared by value. */
export function amountsAfterBillDiscount(amounts: readonly Paisa[], billDiscount: Paisa): Paisa[] {
  const total = amounts.reduce((a, b) => a + b, 0)
  if (billDiscount <= 0 || total <= 0) return [...amounts]
  const shares = allocateProportionally(Math.min(billDiscount, total), amounts)
  return amounts.map((a, i) => a - (shares[i] ?? 0))
}

/**
 * What one unit of each line really cost: its amount, after the bill's discount, over everything
 * that came in. 0 for a line that cost nothing. A line with nothing extra keeps its rate exactly.
 */
export function receiptTrueCosts(lines: readonly ReceiptLineMoney[], billDiscount: Paisa): Paisa[] {
  const after = amountsAfterBillDiscount(lines.map(receiptLineAmount), billDiscount)
  return lines.map((l, i) => {
    const units = l.qtyMilli + l.freeMilli
    const plain =
      !l.freeMilli &&
      !l.discountPaisa &&
      !l.freeChargePaisa &&
      l.amountPaisa == null &&
      billDiscount <= 0
    if (plain) return l.ratePaisa
    return units > 0 ? mulDivRound(after[i] ?? 0, 1000, units) : 0
  })
}

export interface ReturnCosts {
  /** Per unit of the paid goods, after every discount. */
  paidPaisa: Paisa
  /** Per unit of the free goods: only what was charged on them. */
  freePaisa: Paisa
}

/** What going back is worth, per unit, for each stored line of a receipt. */
export function receiptReturnCosts(
  lines: readonly {
    qty_milli: Milli
    unit_cost_paisa: Paisa
    free_qty_milli?: Milli
    free_charge_paisa?: Paisa
    amount_paisa?: Paisa | null
  }[],
  billDiscount: Paisa,
): ReturnCosts[] {
  const amounts = lines.map(storedLineAmount)
  const after = amountsAfterBillDiscount(amounts, billDiscount)
  return lines.map((l, i) => {
    const amount = amounts[i] ?? 0
    const net = after[i] ?? 0
    if (l.amount_paisa == null && billDiscount <= 0)
      return { paidPaisa: l.unit_cost_paisa, freePaisa: 0 }
    const charge = amount > 0 ? mulDivRound(l.free_charge_paisa ?? 0, net, amount) : 0
    return {
      paidPaisa: l.qty_milli > 0 ? mulDivRound(net - charge, 1000, l.qty_milli) : 0,
      freePaisa: l.free_qty_milli ? mulDivRound(charge, 1000, l.free_qty_milli) : 0,
    }
  })
}

/** A saved line, as far as its money goes. */
export interface StoredReceiptLine {
  id: string
  qty_milli: Milli
  unit_cost_paisa: Paisa
  free_qty_milli: Milli
  free_charge_paisa: Paisa
  free_charge_bp: number | null
  discount_paisa: Paisa
  discount_bp: number | null
  amount_paisa: Paisa | null
  entered_qty_milli: Milli | null
}

const storedMoney = (l: StoredReceiptLine): ReceiptLineMoney => ({
  qtyMilli: l.qty_milli,
  freeMilli: l.free_qty_milli,
  ratePaisa: l.unit_cost_paisa,
  discountPaisa: l.discount_paisa,
  freeChargePaisa: l.free_charge_paisa,
  amountPaisa: l.amount_paisa,
})

/** The rate as typed on the bill: per carton on a line entered in cartons. */
export function typedRatePaisa(l: StoredReceiptLine): Paisa {
  if (l.amount_paisa == null || l.entered_qty_milli == null || !l.entered_qty_milli)
    return l.unit_cost_paisa
  if (l.entered_qty_milli === l.qty_milli) return l.unit_cost_paisa
  const gross = l.amount_paisa - l.free_charge_paisa + l.discount_paisa
  return mulDivRound(gross, 1000, l.entered_qty_milli)
}

export interface CorrectedReceipt {
  /** The corrected line's new money. */
  line: {
    unit_cost_paisa: Paisa
    amount_paisa: Paisa | null
    discount_paisa: Paisa
    free_charge_paisa: Paisa
  }
  amountPaisa: Paisa
  billDiscountPaisa: Paisa
  /** Each line's true cost, in the order given, before and after. */
  trueCostsBefore: Paisa[]
  trueCostsAfter: Paisa[]
  totalPaisa: Paisa
}

/**
 * A saved receipt with one line's rate corrected (docs/plans/receipt-corrections.md). A percent
 * discount or charge follows the new rate; an exact one stays. Quantities never change.
 */
export function correctReceiptLine(
  lines: readonly StoredReceiptLine[],
  bill: { discount_paisa: Paisa; discount_bp: number | null },
  lineId: string,
  typedRate: Paisa,
): CorrectedReceipt {
  const target = lines.find((l) => l.id === lineId)
  if (!target) throw new Error('line not on this receipt')
  const typedQty = target.entered_qty_milli || target.qty_milli
  const converted = typedQty !== target.qty_milli
  const scale = (milli: Milli) =>
    converted ? mulDivRound(milli, typedQty, target.qty_milli) : milli
  const discount =
    target.discount_bp !== null
      ? shareOfLine(typedQty, typedRate, target.discount_bp)
      : target.discount_paisa
  const charge =
    target.free_charge_bp !== null
      ? shareOfLine(scale(target.free_qty_milli), typedRate, target.free_charge_bp)
      : target.free_charge_paisa
  const amountPaisa = receiptLineAmount({
    qtyMilli: typedQty,
    ratePaisa: typedRate,
    discountPaisa: discount,
    freeChargePaisa: charge,
  })
  const line = {
    unit_cost_paisa: converted ? mulDivRound(typedRate, typedQty, target.qty_milli) : typedRate,
    amount_paisa: converted || target.free_qty_milli || discount || charge ? amountPaisa : null,
    discount_paisa: discount,
    free_charge_paisa: charge,
  }
  const after = lines.map((l) => (l === target ? storedMoney({ ...l, ...line }) : storedMoney(l)))
  const linesTotal = after.reduce((sum, m) => sum + receiptLineAmount(m), 0)
  const billOff =
    bill.discount_bp !== null
      ? billDiscountPaisa(linesTotal, { mode: 'percent', bp: bill.discount_bp, paisa: 0 })
      : Math.min(bill.discount_paisa, linesTotal)
  return {
    line,
    amountPaisa,
    billDiscountPaisa: billOff,
    trueCostsBefore: receiptTrueCosts(lines.map(storedMoney), bill.discount_paisa),
    trueCostsAfter: receiptTrueCosts(after, billOff),
    totalPaisa: linesTotal - billOff,
  }
}
